import { useState, useRef, useCallback } from "react";
import { useScenario } from "../state";
import { exportScenarioToJson, parseScenarioJson } from "./json-export";
import { exportScenarioToCsv } from "./csv-export";
import { parseScenarioCsv } from "./csv-import";
import { validateScenario } from "./scenario-validator";
import { toastGlobal } from "../toast";
import { Model } from "../model";

export function ImportExportUI() {
  const { active, importScenario } = useScenario();
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const downloadJson = () => {
    const blob = new Blob([exportScenarioToJson(active)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download =
      `${active.assumptions.projectName}_${active.name}.json`.replace(
        /\s+/g,
        "_",
      );
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCsv = () => {
    const blob = new Blob([exportScenarioToCsv(active)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${active.assumptions.projectName}_${active.name}.csv`.replace(
      /\s+/g,
      "_",
    );
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = useCallback(
    async (file: File) => {
      try {
        const text = await file.text();
        const isJson = file.name.toLowerCase().endsWith(".json");
        const isCsv = file.name.toLowerCase().endsWith(".csv");

        if (!isJson && !isCsv) {
          toastGlobal("Please upload a .json or .csv file", "error", 3000);
          return;
        }

        let scenarioToImport: unknown;

        if (isJson) {
          const parsed = parseScenarioJson(text);
          scenarioToImport = parsed.scenario;
        } else {
          const rows = parseScenarioCsv(text);
          const assumptionsOverride: Record<string, unknown> = {};
          for (const row of rows) {
            if (row.group === "assumptions") {
              let val: unknown = row.value;
              const num = Number(row.value);
              if (!Number.isNaN(num) && row.value.trim() !== "") {
                val = num;
              } else if (row.value === "true") {
                val = true;
              } else if (row.value === "false") {
                val = false;
              }
              if (row.value.startsWith("{") || row.value.startsWith("[")) {
                try {
                  val = JSON.parse(row.value);
                } catch {
                  /* keep as string */
                }
              }
              assumptionsOverride[row.key] = val;
            }
          }
          const base = Model.baseScenario();
          scenarioToImport = {
            ...base,
            assumptions: { ...base.assumptions, ...assumptionsOverride },
          };
        }

        const validation = validateScenario(scenarioToImport);
        if (!validation.valid) {
          const firstError = validation.errors[0];
          toastGlobal(
            `Import failed: ${firstError?.message ?? "Invalid scenario"}`,
            "error",
            4000,
          );
          return;
        }

        const payload = JSON.stringify({
          version: 1,
          exportedAt: new Date().toISOString(),
          scenario: scenarioToImport,
        });

        importScenario(payload);
        toastGlobal("Scenario imported successfully", "success", 3000);
      } catch (err) {
        toastGlobal(
          `Import failed: ${err instanceof Error ? err.message : "Unknown error"}`,
          "error",
          4000,
        );
      }
    },
    [importScenario],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      if (inputRef.current) inputRef.current.value = "";
    },
    [handleFile],
  );

  return (
    <div className="row gap-2" style={{ alignItems: "center" }}>
      <button
        type="button"
        className="btn ghost"
        data-testid="export-json-btn"
        onClick={downloadJson}
        title="Export scenario as JSON"
      >
        {"{}"} JSON
      </button>
      <button
        type="button"
        className="btn ghost"
        data-testid="export-csv-btn"
        onClick={downloadCsv}
        title="Export scenario as CSV"
      >
        CSV
      </button>
      <div
        data-testid="import-drop-zone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{
          padding: "6px 12px",
          borderRadius: 8,
          border: `1px dashed ${dragOver ? "hsl(var(--c-primary))" : "hsl(var(--c-border))"}`,
          fontSize: 13,
          color: "hsl(var(--c-foreground-muted))",
          cursor: "pointer",
        }}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          data-testid="import-file-input"
          accept=".json,.csv"
          style={{ display: "none" }}
          onChange={onInputChange}
        />
        Drop file or click to import
      </div>
    </div>
  );
}
